// PackageSizeStep — brand-aware size picker for the AddItem flow.
//
// Three tiers of size suggestions stacked top-down:
//   1. Brand-specific pills    "Kerrygold 8 oz", "Kerrygold 1 lb"
//   2. Canonical-wide pills    "8 oz", "16 oz" (any brand of butter)
//   3. AI typicalSizes         from ingredient_info.package.typicalSizes
//
// All three tiers come from a single `usePopularPackages(brand,
// canonicalId)` call — that hook already does the merge + de-dup +
// AI-fallback wiring (src/lib/usePopularPackages.js). We just render.
//
// Below the pills: a pair of inputs for custom amount + unit,
// always editable. Tapping a pill fills the inputs (so the user
// can confirm and tweak). Hitting "Use" commits.
//
// Why a separate component:
//   - Used by every pipeline (Manual, Wizard, Photo, Scan, Shop Mode)
//     that needs to set a package size.
//   - The brand→size dependency is non-obvious; centralizing avoids
//     each pipeline reimplementing the brand-pair fetch.
//
// Embedded inside ChipModal when used as an axis picker, OR rendered
// inline in pipelines that flow size as a step.

import { useState, useEffect, useMemo } from "react";
import { usePopularPackages } from "../../../lib/usePopularPackages";
import { countUnitInfo } from "../../../lib/countUnitInfo";
import { font, color, radius, space } from "../../../lib/mcmTokens";
import { CHIP_TONES } from "../../../lib/tokens";

// Common units the user can pick from when typing a custom size.
// Ordered by frequency in the pantry domain.
const UNIT_OPTIONS = [
  { id: "oz",       label: "oz" },
  { id: "lb",       label: "lb" },
  { id: "g",        label: "g" },
  { id: "kg",       label: "kg" },
  { id: "fl_oz",    label: "fl oz" },
  { id: "ml",       label: "ml" },
  { id: "l",        label: "L" },
  { id: "cup",      label: "cup" },
  { id: "pint",     label: "pt" },
  { id: "quart",    label: "qt" },
  { id: "gallon",   label: "gal" },
  { id: "count",    label: "count" },
  { id: "package",  label: "package" },
];

const accent = CHIP_TONES.canonical.fg;

// −/+ buttons for the count stepper. Sized for thumbs (44px), same
// surface treatment as the custom-size inputs below.
const countStepBtnStyle = {
  width: 44,
  height: 44,
  flex: "0 0 auto",
  fontFamily: font.sans,
  fontSize: 20,
  lineHeight: 1,
  color: color.ink,
  background: color.cream,
  border: `1px solid ${color.hairline}`,
  borderRadius: radius.field || 12,
  cursor: "pointer",
};

/**
 * Props:
 *   canonicalId — required
 *   brand       — optional; drives Tier-1 pills
 *   value       — { amount, unit } or null. Current store value.
 *   onChange    — (next: { amount, unit }) => void. Fires on commit.
 *   onCommit    — () => void. Optional; called after user picks.
 *                 Used by ChipModal callers to close the sheet.
 *   compact     — boolean. When true, suppresses the Use button and
 *                 commits inline-on-change (for inline embeds).
 */
export default function PackageSizeStep({
  canonicalId,
  brand = null,
  value = null,
  onChange,
  onCommit,
  compact = false,
}) {
  const { rows, loading } = usePopularPackages(brand, canonicalId, 8);
  const [amount, setAmount] = useState(value?.amount != null ? String(value.amount) : "");
  const [unit, setUnit] = useState(value?.unit || "oz");

  // Count-aware mode — derived from the canonical itself (no new
  // prop), so every mount site (AddItem wizard, ResolveCard, ChipModal
  // embeds) gets the "how many chops?" stepper automatically for
  // count-default canonicals like lamb_chop or eggs.
  const count = countUnitInfo(canonicalId);
  const countMode = count.isCountDefault;
  const countNoun = count.label || "items";
  const [countN, setCountN] = useState(() => {
    const seed = value?.unit === "count" ? Number(value.amount) : NaN;
    return Number.isFinite(seed) && seed >= 1 ? Math.min(99, Math.round(seed)) : 1;
  });

  useEffect(() => {
    if (value?.amount != null) setAmount(String(value.amount));
    if (value?.unit) setUnit(value.unit);
  }, [value?.amount, value?.unit]);

  // Split rows into brand-specific and canonical-wide for visual
  // grouping. Tier 3 (AI) lands inside `generic` already since the
  // hook flags `source: "ai"` but mixes by amount/unit dedup.
  const { specific, generic } = useMemo(() => {
    const sp = [];
    const gen = [];
    for (const r of rows) {
      if (brand && r.brand && r.brand === brand) sp.push(r);
      else gen.push(r);
    }
    // Count-default canonicals lead with count-unit pills — "6 chops"
    // rates above "1.5 lb" when the item is bought by the piece.
    // Array.prototype.sort is stable, so ties keep hook order.
    if (countMode) {
      const countFirst = (a, b) =>
        (b.unit === "count" ? 1 : 0) - (a.unit === "count" ? 1 : 0);
      sp.sort(countFirst);
      gen.sort(countFirst);
    }
    return { specific: sp, generic: gen };
  }, [rows, brand, countMode]);

  function pickPill(r) {
    setAmount(String(r.amount));
    setUnit(r.unit);
    // Keep the count stepper in sync when a count pill is picked, so
    // the confirm button doesn't quietly commit a stale count.
    if (r.unit === "count" && Number.isFinite(Number(r.amount)) && r.amount >= 1) {
      setCountN(Math.min(99, Math.round(Number(r.amount))));
    }
    if (compact) {
      onChange?.({ amount: r.amount, unit: r.unit });
      onCommit?.();
    }
  }

  function commitTyped() {
    const n = parseFloat(amount);
    if (!Number.isFinite(n) || n <= 0) return;
    onChange?.({ amount: n, unit });
    onCommit?.();
  }

  function commitCount() {
    setAmount(String(countN));
    setUnit("count");
    onChange?.({ amount: countN, unit: "count" });
    onCommit?.();
  }

  const canCommit = (() => {
    const n = parseFloat(amount);
    return Number.isFinite(n) && n > 0 && unit;
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Count stepper — count-default canonicals ask "how many
          chops?" first. Suggestion pills stay below as shortcuts and
          the custom amount/unit row stays as the by-weight escape
          hatch (butcher-counter lamb sold in lb still types "1.2 lb"). */}
      {countMode && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{
            fontFamily: font.mono,
            fontSize: 12,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: color.ink,
          }}>
            How many {countNoun}?
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              onClick={() => setCountN((n) => Math.max(1, n - 1))}
              aria-label={`One fewer ${countNoun}`}
              style={countStepBtnStyle}
            >
              −
            </button>
            <div style={{
              minWidth: 44,
              textAlign: "center",
              fontFamily: font.serif,
              fontStyle: "italic",
              fontSize: 20,
              color: color.ink,
            }}>
              {countN}
            </div>
            <button
              type="button"
              onClick={() => setCountN((n) => Math.min(99, n + 1))}
              aria-label={`One more ${countNoun}`}
              style={countStepBtnStyle}
            >
              +
            </button>
            <button
              type="button"
              onClick={commitCount}
              style={{
                flex: 1,
                padding: "10px 12px",
                fontFamily: font.sans,
                fontSize: 14,
                fontWeight: 600,
                color: color.cream,
                background: color.ink,
                border: `1px solid ${color.ink}`,
                borderRadius: radius.field || 12,
                cursor: "pointer",
              }}
            >
              Use {countN} {countNoun}
            </button>
          </div>
        </div>
      )}
      {/* Tier 1 — brand-specific pills */}
      {brand && specific.length > 0 && (
        <PillRow
          kicker={`${brand.toUpperCase()} SIZES`}
          pills={specific}
          onPick={pickPill}
          activeAmount={amount}
          activeUnit={unit}
          countLabel={countMode ? count.label : null}
          highlight
        />
      )}
      {/* Tier 2/3 — canonical-wide + AI pills */}
      {generic.length > 0 && (
        <PillRow
          kicker={specific.length > 0 ? "OTHER SIZES" : "POPULAR SIZES"}
          pills={generic}
          onPick={pickPill}
          activeAmount={amount}
          activeUnit={unit}
          countLabel={countMode ? count.label : null}
        />
      )}
      {loading && rows.length === 0 && (
        <div style={{
          fontFamily: font.detail,
          fontStyle: "italic",
          fontSize: 13,
          color: color.inkMuted,
          textAlign: "center",
          padding: "8px 0",
        }}>
          Loading suggestions…
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div style={{
          fontFamily: font.detail,
          fontStyle: "italic",
          fontSize: 13,
          color: color.inkMuted,
          textAlign: "center",
          padding: "8px 0",
        }}>
          No popular sizes yet — tell us how it's packaged.
        </div>
      )}
      {/* Custom input row */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}>
        <div style={{
          fontFamily: font.mono,
          fontSize: 10,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: color.inkMuted,
        }}>
          Custom size
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="amount"
            style={{
              flex: 1,
              minWidth: 0,
              padding: "10px 12px",
              fontFamily: font.serif,
              fontStyle: "italic",
              fontSize: 16,
              color: color.ink,
              background: color.cream,
              border: `1px solid ${color.hairline}`,
              borderRadius: radius.field || 12,
              outline: "none",
            }}
          />
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            style={{
              padding: "10px 12px",
              fontFamily: font.sans,
              fontSize: 14,
              color: color.ink,
              background: color.cream,
              border: `1px solid ${color.hairline}`,
              borderRadius: radius.field || 12,
              outline: "none",
              cursor: "pointer",
            }}
          >
            {UNIT_OPTIONS.map((u) => (
              <option key={u.id} value={u.id}>{u.label}</option>
            ))}
          </select>
        </div>
      </div>
      {!compact && (
        <button
          onClick={commitTyped}
          disabled={!canCommit}
          style={{
            padding: "12px 16px",
            fontFamily: font.sans,
            fontSize: 14,
            fontWeight: 600,
            color: canCommit ? color.cream : color.inkFaint,
            background: canCommit ? color.ink : "transparent",
            border: `1px solid ${canCommit ? color.ink : color.hairline}`,
            borderRadius: radius.field || 12,
            cursor: canCommit ? "pointer" : "not-allowed",
            transition: "background 120ms ease, color 120ms ease",
          }}
        >
          Use this size
        </button>
      )}
    </div>
  );
}

function PillRow({ kicker, pills, onPick, activeAmount, activeUnit, countLabel = null, highlight }) {
  if (!pills || pills.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{
        fontFamily: font.mono,
        fontSize: 11,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: highlight ? color.ink : color.inkMuted,
      }}>
        {kicker}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {pills.map((r, i) => {
          const active =
            String(r.amount) === String(activeAmount) && r.unit === activeUnit;
          return (
            <button
              key={`${r.amount}-${r.unit}-${i}`}
              onClick={(e) => { e.preventDefault(); onPick(r); }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "6px 10px",
                fontFamily: font.mono,
                fontSize: 11,
                letterSpacing: "0.06em",
                color: active ? color.cream : color.ink,
                background: active
                  ? accent
                  : (highlight ? `${accent}1f` : color.cream),
                border: `1px solid ${active ? accent : color.hairline}`,
                borderRadius: 999,
                cursor: "pointer",
              }}
            >
              {formatSize(r.amount, r.unit, r.countLabel || countLabel)}
              {r.n > 1 && !active && (
                <span style={{
                  fontSize: 9,
                  color: color.inkFaint,
                  marginLeft: 2,
                }}>
                  ×{r.n}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function formatSize(amount, unit, countLabel = null) {
  if (amount == null || !unit) return "";
  // Count pills read with the noun ("4 chops") when we know it —
  // either carried on the row (AI typicalSizes parse) or derived from
  // the canonical's count-unit label. Display-only; storage is "count".
  const u = (unit === "count" && countLabel)
    ? countLabel
    : UNIT_OPTIONS.find((o) => o.id === unit)?.label || unit;
  // Drop trailing .0 for whole numbers
  const a = Number.isInteger(amount) ? String(amount) : String(amount);
  return `${a} ${u}`;
}
